import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { isFirefox, isWindows } from 'react-device-detect';
import styled from 'styled-components';
import { useHasSupportedVideoCodec } from '../../hooks/useHasSupportedVideoCodec';
import { useTrackBroadcastDurationEffect } from '../../hooks/useTrackBroadcastDurationEffect';
import { useWakeLock } from '../../hooks/useWakeLock';
import { BroadcastStateService } from '../../services/broadcaststate/BroadcastStateService';
import { ViewerService } from '../../services/viewer/ViewerService';
import { copyToClipboard } from '../../utils/copyToClipboard';
import { useTimedFlag } from '../../utils/time';
import { formBroadcastUrl } from '../../utils/url';
import { Canvas } from '../Canvas';
import { FullscreenButton } from '../FullscreenButton';
import { ViewerViewHud } from '../huds/ViewerViewHud';
import { LoadingIndicator } from '../LoadingIndicator';
import { CodecNotInstalled, CodecNotSupported, FirefoxNotSupported, } from '../NotSupportedMessage';
import { showToast } from '../popups/ToastPopup';
import { SidebarSlot } from '../SidebarSlot';
import { Stage, StageContainer } from '../Stage';
import { WorkspaceControls } from '../shared/WorkspaceControls';
import { SplitShell } from '../shells/SplitShell';
import { StackShell } from '../shells/StackShell';
import { VideoDisplay } from '../VideoDisplay';
import { ViewerPreLive } from '../ViewerPreLive';
import { ViewerFooter } from '../viewer/ViewerFooter';
import { ViewerHeader } from '../viewer/ViewerHeader';
export function ViewerView() {
    const activeBroadcast = BroadcastStateService.getOutput().useRequiredActiveBroadcast();
    const ready = ViewerService.getOutput().useReady();
    const isVideoStreaming = ViewerService.getOutput().useIsVideoStreaming();
    const isAudioOnMute = ViewerService.getOutput().useAudioMuted();
    const [showSidebar, setShowSidebar] = useState(true);
    useTrackBroadcastDurationEffect({
        enabled: isVideoStreaming,
        action: 'Watch',
    });
    const browserVideoCodecSupported = useHasSupportedVideoCodec();
    const stageContainerRef = useRef(null);
    const [isHudVisible, showHud] = useTimedFlag(true, 5000);
    useWakeLock({ enabled: true });
    const broadcastUrl = formBroadcastUrl(activeBroadcast);
    if (isFirefox) {
        return _jsx(FirefoxNotSupported, {});
    }
    if (browserVideoCodecSupported === false) {
        if (isWindows) {
            return _jsx(CodecNotInstalled, {});
        }
        return _jsx(CodecNotSupported, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: !ready }), _jsx(ViewerViewRoot, { onTouchStart: () => {
                    showHud();
                }, onMouseDown: () => {
                    showHud();
                }, onMouseOver: () => {
                    showHud();
                }, before: _jsx(ViewerHeader, { controls: _jsx(WorkspaceControls, { sidebarActive: showSidebar, onSidebarClick: () => {
                            setShowSidebar((c) => !c);
                        }, onLinkClick: () => {
                            copyToClipboard(broadcastUrl);
                            showToast('Link copied!');
                        } }) }), after: _jsx(ViewerFooter, { audioMuted: isAudioOnMute }), children: _jsx(SplitShell, { isSidebarOpen: showSidebar, sidebar: _jsx(SidebarSlot, {}), children: _jsx(StageContainer, { ref: stageContainerRef, children: _jsxs(Stage, { children: [_jsx(VideoDisplay, { fit: "contain", component: _jsx(Canvas, {}), hud: _jsx(ViewerViewHud, { visible: isHudVisible, fullscreenButton: _jsx(FullscreenButton, { element: stageContainerRef.current }) }) }), !isVideoStreaming && (_jsx(ViewerPreLive, { broadcastStreamId: activeBroadcast.streamId, broadcasterUserId: activeBroadcast.broadcasterUserId }))] }) }) }) })] }));
}
const ViewerViewRoot = styled(StackShell) `
    --tv-ui-VideoContainer-video-border-radius: var(--tv-ui-border-radius-1);
    height: 100vh;

    @media (min-width: 60rem) {
        --SplitShell_SidebarWidth: 25.25rem;
    }
`;
